<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3'
import { computed, ref, nextTick } from 'vue'
import PageHeading from "@/Components/Headings/PageHeading.vue"
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Tag from '@/Components/Utils/Tag.vue'
import Icon from '@/Components/Utils/Icon.vue'
import Button from '@/Components/Utils/Button.vue'
import ModalConfirmationDelete from '@/Components/Utils/ModalConfirmationDelete.vue'
import CreateComplaintModal from '@/Components/Master/Complaints/CreateComplaintModal.vue'
import { useFormatTime } from '@/Composables/useFormatTime'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { notify } from '@kyvg/vue3-notification'
import { router } from '@inertiajs/vue3'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faPencil, faTimes, faCheck, faCommentAltExclamation } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'
library.add(faPencil, faTimes, faCheck, faCommentAltExclamation)

const props = defineProps<{
    pageHead?: PageHeadingTS
    title?: string
    canCreateComplaint?: boolean
    data: {
        id: number
        slug: string
        reference: string
        state: string
        state_label: string
        state_icon: { icon: string, class: string, tooltip: string }
        type: string
        type_icon: { icon: string, class: string, tooltip: string }
        quantity: number
        created_by: { id: number, name: string }
        customer: { id: number, name: string, slug: string } | null
        product: { id: number, name: string, code: string, price: number, type: string } | null
        product_snapshot: { name: string, code: string, price: number, type: string } | null
        total_amount: number
        dimension_width: number | null
        dimension_height: number | null
        billing_status: string
        billing_status_label: string
        billing_date: string | null
        item_position: string | null
        file_merek: string | null
        note: string | null
        is_unpaid: boolean
        current_product_price: number | null
        customer_product_price: number | null
        snapshot_price: number | null
        invoice: { id: number; slug: string; reference: string } | null
        created_at: string
        manufacture_finished_by: string
    }
}>()

const effectiveCurrentPrice = computed(() =>
    props.data.customer_product_price ?? props.data.current_product_price
)

const previewTotalAfterRefresh = computed(() => {
    if (effectiveCurrentPrice.value === null) return null
    if (props.data.type === 'flexo' && props.data.dimension_width && props.data.dimension_height) {
        return props.data.quantity * props.data.dimension_width * props.data.dimension_height * effectiveCurrentPrice.value
    }
    return props.data.quantity * effectiveCurrentPrice.value
})

const hasPriceDiff = computed(() =>
    props.data.is_unpaid &&
    effectiveCurrentPrice.value !== null &&
    props.data.snapshot_price !== null &&
    effectiveCurrentPrice.value !== props.data.snapshot_price
)

const isLoadingRefreshPrice = ref(false)
const refreshPrice = () => {
    router.patch(route('casheer.orders.refresh_price', props.data.id), {}, {
        onStart: () => isLoadingRefreshPrice.value = true,
        onSuccess: () => notify({ title: 'Berhasil', text: 'Harga berhasil diperbarui!', type: 'success' }),
        onFinish: () => isLoadingRefreshPrice.value = false,
    })
}

const isComplaintModalVisible = ref(false)

const isEditingPosition = ref(false)
const editPositionValue = ref('')
const isLoadingPosition = ref(false)
const positionInputRef = ref<HTMLInputElement | null>(null)

const startEditPosition = async () => {
    editPositionValue.value = props.data.item_position ?? ''
    isEditingPosition.value = true
    await nextTick()
    positionInputRef.value?.focus()
    positionInputRef.value?.select()
}

const cancelEditPosition = () => {
    isEditingPosition.value = false
}

const savePosition = () => {
    isLoadingPosition.value = true
    router.patch(
        route('casheer.orders.update', props.data.id),
        { item_position: editPositionValue.value || null },
        {
            preserveState: true,
            preserveScroll: true,
            onSuccess: () => {
                isEditingPosition.value = false
                notify({ title: 'Berhasil', text: 'Posisi barang diperbarui.', type: 'success' })
            },
            onError: () => notify({ title: 'Gagal', text: 'Gagal memperbarui posisi barang.', type: 'error' }),
            onFinish: () => isLoadingPosition.value = false,
        }
    )
}
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead">
        <template #button-delete-order="{ action }">
            <ModalConfirmationDelete
                :routeDelete="action.route"
                :title="'Yakin ingin menghapus order ' + data.reference + '?'"
                description="Order akan dihapus permanen."
                xisFullLoading
                @success="() => notify({ title: 'Berhasil', text: 'Order berhasil dihapus!', type: 'success' })"
                @onError="(e) => notify({
                    title: 'Gagal menghapus order',
                    text: e.order || 'Terjadi kesalahan saat menghapus order.',
                    type: 'error',
                })"
            >
                <template #default="{ changeModel }">
                    <Button
                        @click="changeModel()"
                        :style="action.style"
                        :label="action.label"
                        :icon="action.icon"
                        :key="`ActionButton${action.label}${action.style}`"
                        :tooltip="action.tooltip"
                    />
                </template>
            </ModalConfirmationDelete>
        </template>
    </PageHeading>

    <div class="mx-8 max-w-2xl mt-6 space-y-6">
        <!-- Status badges -->
        <div class="flex items-center justify-between gap-x-3">
            <div class="flex items-center gap-x-3">
                <div class="flex items-center gap-x-1.5 text-sm font-medium">
                    <Icon :data="data.state_icon" />
                    <span>{{ data.state_label }}</span>
                </div>
                <Tag
                    :label="data.billing_status_label"
                    :theme="data.billing_status === 'paid' ? 1 : 8"
                    noHoverColor
                />
            </div>
            <Button
                v-if="canCreateComplaint"
                v-tooltip="'Buat komplain terkait order ini'"
                type="red"
                label="Buat Komplain"
                icon="fal fa-comment-alt-exclamation"
                @click="isComplaintModalVisible = true"
            />
        </div>

        <!-- Refresh price button (only when price differs from current) -->
        <div v-if="hasPriceDiff" class="rounded-lg border border-amber-200 bg-amber-50 p-4 flex flex-col gap-3">
            <div class="flex items-start gap-2 text-sm text-amber-800">
                <span class="mt-0.5">⚠️</span>
                <div class="space-y-2">
                    <p class="font-semibold">Harga produk tidak sesuai dengan harga saat ini</p>
                    <table class="text-xs text-amber-800 border-separate border-spacing-y-1">
                        <tr>
                            <td class="pr-4 text-amber-600">Harga saat order dibuat</td>
                            <td class="font-semibold">{{ useFormatCurrency(data.snapshot_price!) }}</td>
                        </tr>
                        <tr>
                            <td class="pr-4 text-amber-600">Harga umum produk saat ini</td>
                            <td class="font-semibold">{{ data.current_product_price != null ? useFormatCurrency(data.current_product_price) : '-' }}</td>
                        </tr>
                        <tr v-if="data.customer_product_price != null">
                            <td class="pr-4 text-amber-600">Harga khusus pelanggan ini</td>
                            <td class="font-semibold text-amber-900">{{ useFormatCurrency(data.customer_product_price) }}</td>
                        </tr>
                    </table>
                    <p class="text-amber-700">
                        Harga yang akan dipakai setelah refresh adalah
                        <span class="font-bold">
                            {{ effectiveCurrentPrice != null ? useFormatCurrency(effectiveCurrentPrice!) : '-' }}
                        </span>
                        <span v-if="data.customer_product_price != null"> (harga khusus pelanggan)</span>
                        <span v-else> (harga umum produk)</span>.
                    </p>
                    <div class="flex items-center gap-4 pt-1">
                        <div class="text-center">
                            <p class="text-xs text-amber-600 mb-0.5">Total saat ini</p>
                            <p class="font-semibold text-amber-800 line-through">{{ useFormatCurrency(data.total_amount) }}</p>
                        </div>
                        <span class="text-amber-500">→</span>
                        <div class="text-center">
                            <p class="text-xs text-amber-600 mb-0.5">Total setelah refresh</p>
                            <p class="font-bold text-amber-900">{{ previewTotalAfterRefresh != null ? useFormatCurrency(previewTotalAfterRefresh!) : '-' }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <Button
                style="secondary"
                label="Update sesuai harga terbaru"
                icon="fal fa-sync"
                :loading="isLoadingRefreshPrice"
                @click="refreshPrice"
            />
        </div>

        <!-- Detail card -->
        <div class="w-full overflow-hidden rounded-xl border border-sky-400">
            <div class="flex flex-col gap-y-1 border-b border-gray-900/5 bg-gray-50 p-6">
                <div class="flex items-center gap-x-2 font-bold text-lg">
                    <Icon :data="data.type_icon" />
                    <span>{{ data.reference }}</span>
                </div>
                <div v-tooltip="'Tgl. dibuat'" class="text-sm text-gray-500 w-fit">
                    {{ useFormatTime(data.created_at) }}
                </div>
            </div>

            <dl class="-my-3 divide-y divide-gray-100 px-6 py-4 text-sm/6">
                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Pelanggan</dt>
                    <dd class="font-medium">
                        <Link v-if="data.customer" :href="route('casheer.customers.show', data.customer.slug)" class="primaryLink">
                            {{ data.customer.name }}
                        </Link>
                        <span v-else>-</span>
                    </dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Produk</dt>
                    <dd class="font-medium">
                        <span>{{ data.product_snapshot?.name ?? '-' }}</span>
                        <span v-if="data.type === 'flexo' && data.dimension_width && data.dimension_height" class="ml-1 text-gray-400 font-normal">
                            ({{ data.dimension_width }} × {{ data.dimension_height }} cm)
                        </span>
                    </dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Tipe</dt>
                    <dd class="flex items-center gap-x-1.5 font-medium">
                        <Icon :data="data.type_icon" />
                        {{ data.type_icon?.tooltip }}
                    </dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Jumlah produk yang dipesan</dt>
                    <dd class="font-medium">{{ data.quantity }}</dd>
                </div>

                <div v-if="data.type === 'flexo'" class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Dimensi (W × H)</dt>
                    <dd class="font-medium">{{ data.dimension_width }} × {{ data.dimension_height }} cm</dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Harga Satuan</dt>
                    <dd class="font-medium flex flex-col items-end gap-0.5">
                        <span>{{ data.product_snapshot?.price != null ? useFormatCurrency(data.product_snapshot.price) : '-' }}</span>
                        <span v-if="data.customer_product_price == data.product_snapshot?.price" class="text-xs text-sky-600">(harga khusus pelanggan)</span>
                        <span v-else-if="data.product_snapshot?.price == data.current_product_price" class="text-xs text-gray-400">(harga umum produk)</span>
                    </dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Total</dt>
                    <dd class="font-semibold">{{ useFormatCurrency(data.total_amount) }}</dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Posisi Barang</dt>
                    <dd class="font-medium">
                        <!-- Edit mode -->
                        <div v-if="isEditingPosition" class="flex items-center gap-1.5">
                            <input
                                ref="positionInputRef"
                                v-model="editPositionValue"
                                type="text"
                                maxlength="255"
                                placeholder="Kosongkan untuk hapus..."
                                class="rounded border border-sky-400 px-2 py-0.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400/30 w-48"
                                @keydown.enter="savePosition"
                                @keydown.escape="cancelEditPosition"
                            />
                            <button
                                @click="savePosition"
                                :disabled="isLoadingPosition"
                                class="text-green-600 hover:text-green-700 disabled:opacity-50 p-0.5"
                                v-tooltip="'Simpan (Enter)'"
                            >
                                <FontAwesomeIcon icon="fal fa-check" class="text-sm" />
                            </button>
                            <button
                                @click="cancelEditPosition"
                                class="text-gray-400 hover:text-gray-600 p-0.5"
                                v-tooltip="'Batal (Esc)'"
                            >
                                <FontAwesomeIcon icon="fal fa-times" class="text-sm" />
                            </button>
                        </div>

                        <!-- Display mode -->
                        <button
                            v-else
                            @click="startEditPosition"
                            class="group flex items-center gap-1.5 hover:text-sky-600 transition-colors text-left"
                            v-tooltip="'Klik untuk mengubah posisi barang'"
                        >
                            <span>{{ data.item_position ?? '-' }}</span>
                            <FontAwesomeIcon icon="fal fa-pencil" class="text-xs text-gray-300 group-hover:text-sky-500 transition-colors" />
                        </button>
                    </dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Dibuat oleh</dt>
                    <dd class="font-medium">{{ data.created_by?.name ?? '-' }}</dd>
                </div>

                <div v-if="data.file_merek" class="flex justify-between gap-x-6 py-3">
                    <dt class="text-gray-500 whitespace-nowrap">File Merek</dt>
                    <dd class="font-medium text-right">{{ data.file_merek }}</dd>
                </div>

                <div v-if="data.note" class="flex flex-col gap-x-4 py-3">
                    <dt class="text-gray-500">
                        Catatan <FontAwesomeIcon icon='fal fa-sticky-note' class='' fixed-width aria-hidden='true' />
                    </dt>
                    <dd class="border px-2 py-1 rounded bg-gray-100/50">{{ data.note }}</dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Invoice</dt>
                    <dd class="font-medium">
                        <Link
                            v-if="data.invoice"
                            :href="route('casheer.invoices.show', data.invoice.slug)"
                            class="text-sky-600 hover:text-sky-800 hover:underline"
                        >
                            {{ data.invoice.reference }}
                        </Link>
                        <span v-else class="text-gray-400 italic">Belum masuk Invoice</span>
                    </dd>
                </div>
            </dl>
        </div>
    </div>

    <CreateComplaintModal
        v-model:visible="isComplaintModalVisible"
        :prefillOrder="{
            id: data.id,
            reference: data.reference,
            customer: data.customer ?? undefined,
            billing_status: data.billing_status,
            billing_status_label: data.billing_status_label,
            type: data.type,
            type_icon: data.type_icon,
            product_name: data.product_snapshot?.name,
            quantity: data.quantity,
            dimension_width: data.dimension_width ?? undefined,
            dimension_height: data.dimension_height ?? undefined,
            created_at: data.created_at,
            manufacture_finished_by: data.manufacture_finished_by,
            file_merek: data.file_merek ?? undefined,
        }"
    />
</template>
